<script setup>
import {defineProps, inject, onMounted, ref, watch} from "vue";
import FileService from "@/services/FileService";
import {useStore} from "vuex";
import {useRouter} from "vue-router";

//general
const store = useStore();
const router = useRouter();
const props = defineProps({
  showFilesTab: Boolean,
});

// eslint-disable-next-line no-unused-vars
const showToastMessage = inject("showToastMessage");

// Pagination and search state
const currentPage = ref(1);
const perPage = ref(10);
const searchKeyword = ref('');
const sortArray = ref([]);

onMounted(() => {
  listFiles();
});

const listFiles = (page = null, rows = null, search = null, sort = null) => {
  table_loading.value = true;

  // Use provided parameters or current state
  const requestPage = page !== null ? page : currentPage.value;
  const requestPerPage = rows !== null ? rows : perPage.value;
  const requestSearch = search !== null ? search : searchKeyword.value;
  const requestSort = sort !== null ? sort : sortArray.value;

  FileService.searchFiles(router, store, requestPage, requestPerPage, requestSearch, requestSort).then((response) => {
    if(response.status === 'error') {
      console.error(response.message);
      showToastMessage('error', 'Error', response.message || 'Failed to load files');
      table_loading.value = false;
      return;
    }

    files.value = response.files.map(file => ({
      ...file,
      id: file.id,
      name: file.name,
      status: file.status,
      statusDisplay: formatStatus(file.status),  // formatted status for search
      deleteStatus: file.delete_status || '',
      processedAt: file.processed_at || '',
      fileDeletedAt: file.file_deleted_at || '',
      studentsCount: file.students_count || 0,
      createdAt: file.created_at || '',
      uploadedBy: file.uploaded_by || '',
      schoolName: file.school_name || '',
    }));

    // Update pagination state
    if (response.pagination) {
      currentPage.value = response.pagination.current_page;
      perPage.value = response.pagination.per_page;
      totalRecords.value = response.pagination.total;
    }

    table_loading.value = false;
  }).catch((error) => {
    console.error(error);
    showToastMessage('error', 'Error', 'Failed to load files');
    table_loading.value = false;
  });
};

// Handle page change event from DataTable
const onPageChange = (event) => {
  currentPage.value = event.page + 1; // PrimeVue uses 0-based page index
  perPage.value = event.rows;
  listFiles(currentPage.value, perPage.value);
};

// Handle sort change from DataTable
const onSort = (event) => {
  const sortFields = [];
  if (event.sortField) {
    sortFields.push({
      field: event.sortField,
      direction: event.sortOrder === 1 ? 'asc' : 'desc'
    });
  }
  sortArray.value = sortFields;
  currentPage.value = 1; // Reset to first page on sort
  listFiles(1, perPage.value, searchKeyword.value, sortFields);
};

// general data table
const files = ref([]);
const filters = ref({
  global: {value: null},
  name: {value: null},
  status: {value: null}
});

// Handle search from global filter with debounce
let searchTimeout = null;
watch(() => filters.value.global.value, (newValue) => {
  if (searchTimeout) {
    clearTimeout(searchTimeout);
  }

  searchTimeout = setTimeout(() => {
    searchKeyword.value = newValue || '';
    currentPage.value = 1;
    listFiles(1, perPage.value, searchKeyword.value);
  }, 500);
});

const table_loading = ref(true);
const totalRecords = ref(0);

const getStatusSeverity = (status) => {
  switch(status) {
    case 'processed': return 'success';        // green
    case 'validated': return 'warn';           // yellow
    case 'uploaded': return 'warn';            // yellow
    case 'validation_failed':
    case 'processing_failed': return 'danger'; // red
    case 'imported': return 'info';            // blue
    default: return 'secondary';
  }
};

const formatStatus = (status) => {
  if (!status) return '';
  // Replace underscores with spaces (lowercase)
  return status.replace(/_/g, ' ');
};

const formatDateTime = (dateTimeString) => {
  if (!dateTimeString) return '';
  const date = new Date(dateTimeString);

  // Format date in Eastern Time (America/Toronto) with timezone abbreviation
  // Example: Nov 20, 2025, 05:20:06 PM EST
  const options = {
    timeZone: 'America/Toronto',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: true,
    timeZoneName: 'short'
  };

  return date.toLocaleString('en-US', options);
};

</script>

<template>
  <div v-if="props.showFilesTab" id="files-page">
    <h1 class="text-3xl font-bold py-8 text-primaryBlue flex items-center">
      File Upload Logs
    </h1>

    <div class="card">
      <DataTable
          id="filesTable"
          v-model:filters="filters"
          :value="files"
          removableSort
          rowHover
          stripedRows
          highlightOnSelect
          :rows="perPage"
          dataKey="id"
          :loading="table_loading"
          paginator
          lazy
          :totalRecords="totalRecords"
          @sort="onSort"
          @page="onPageChange"
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          :rowsPerPageOptions="[10, 20, 30]"
          currentPageReportTemplate="Showing {first} to {last} of {totalRecords} files"
      >
        <template #header>
          <div class="flex justify-start w-[450px]">
            <IconField class="w-full">
              <InputIcon>
                <i class="pi pi-search"/>
              </InputIcon>
              <InputText
                  class="w-full"
                  v-model="filters['global'].value"
                  placeholder="Search"
              />
            </IconField>
          </div>
        </template>
        <template #empty> No files found.</template>
        <template #loading> Loading files data. Please wait.</template>

        <Column field="name" header="File Name" :sortable="true" style="min-width: 12rem">
          <template #body="{ data }">
            {{ data.name }}
          </template>
        </Column>

        <Column field="schoolName" header="School Name" :sortable="true" style="min-width: 12rem">
          <template #body="{ data }">
            <span v-if="data.schoolName">{{ data.schoolName }}</span>
            <span v-else class="text-gray-400">-</span>
          </template>
        </Column>

        <Column field="status" header="Status" :sortable="true" style="min-width: 10rem">
          <template #body="{ data }">
            <Tag
                :severity="getStatusSeverity(data.status)"
                :value="formatStatus(data.status)"
            ></Tag>
          </template>
        </Column>


        <Column field="studentsCount" header="Processed Students" :sortable="true" style="min-width: 8rem">
          <template #body="{ data }">
            {{ data.studentsCount }}
          </template>
        </Column>

        <Column field="processedAt" header="Processed At" :sortable="true" style="min-width: 12rem">
          <template #body="{ data }">
            <span v-if="data.processedAt">{{ formatDateTime(data.processedAt) }}</span>
            <span v-else class="text-gray-400">-</span>
          </template>
        </Column>

        <Column field="createdAt" header="Uploaded At" :sortable="true" style="min-width: 12rem">
          <template #body="{ data }">
            {{ formatDateTime(data.createdAt) }}
          </template>
        </Column>

        <Column field="uploadedBy" header="Uploaded By" :sortable="true" style="min-width: 12rem">
          <template #body="{ data }">
            <span v-if="data.uploadedBy">{{ data.uploadedBy }}</span>
            <span v-else class="text-gray-400">-</span>
          </template>
        </Column>

      </DataTable>

    </div>
  </div>
</template>

<style>
/* Align pagination to the right */
#files-page .p-paginator {
  justify-content: flex-end;
  padding-right: 17px;
}

/* Set minimum row height to accommodate 2 lines of text */
#files-page .p-datatable-tbody > tr > td {
  min-height: 4rem;
  vertical-align: middle;
}

#files-page .p-datatable-tbody > tr {
  min-height: 4rem;
}
</style>
